import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Permission helpers derived from the signed-in user's role and permissions
 * (as returned by GET /api/auth/verify). Mirrors the backend rules:
 * company_admin bypasses everything; otherwise a permission entry for the
 * resource must contain the action or 'manage'.
 */
export const usePermission = () => {
  const { user } = useAuth();

  const hasPermission = useCallback((resource, action = 'read') => {
    if (!user) return false;
    if (user.role === 'company_admin') return true;
    const entry = (user.permissions || []).find((p) => p.resource === resource);
    if (!entry) return false;
    return entry.actions.includes(action) || entry.actions.includes('manage');
  }, [user]);

  const hasAnyPermission = useCallback((pairs) => pairs.some(([r, a]) => hasPermission(r, a)), [hasPermission]);

  return useMemo(() => ({
    user,
    hasPermission,
    hasAnyPermission,
    isCompanyAdmin: !!user && user.role === 'company_admin',
    isPlatformAdmin: !!user && !!user.isPlatformAdmin,
    canManageUsers: hasPermission('users', 'manage'),
    canManageCompany: hasPermission('company', 'manage'),
    canManageAgents: hasAnyPermission([['agents', 'manage'], ['company', 'manage']]),
    canReadAgents: hasAnyPermission([['agents', 'read'], ['company', 'read']]),
    canManageBilling: hasPermission('billing', 'manage'),
    canManageRoles: hasPermission('roles', 'manage') || hasPermission('roles', 'update'),
  }), [user, hasPermission, hasAnyPermission]);
};

export default usePermission;
